import { setCookie } from "cookies-next";

// external dependencies
import { useRouter } from "next/navigation";
import { useState } from "react";
import { useForm } from "react-hook-form";

// types

import { COOKIE_CONFIG } from "@/config";
import { GLOBAL_ERROR_MESSAGE } from "@/constants/validation-message";
import { IApiError } from "@/interface/apiError.types";
import { showToast, TOAST_TYPES } from "@/lib/toast-utils/toast.util";
import loginSchema from "@/schema/login";

// internal utilities and services
import { loginUser } from "@/services/identity/identity.service";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import * as Sentry from "@sentry/nextjs";
import { z } from "zod";
import useProfileStore from "@/store/useProfileStore";

const useLogin = () => {
  const router = useRouter();
  const { setAccessToken } = useProfileStore();

  const form = useForm<z.infer<typeof loginSchema>>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      username: "",
      password: "",
    },
  });

  const [showPassword, setShowPassword] = useState<boolean>(false);

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const { mutate: login, isPending: loginLoading } = useMutation({
    mutationFn: loginUser,
    onSuccess: (response) => {
      const accessToken =
        response?.data?.accessToken ??
        response?.data?.token ??
        response?.accessToken ??
        response?.token;

      if (typeof accessToken === "string" && accessToken.trim()) {
        setAccessToken(accessToken);
      }

      setCookie(COOKIE_CONFIG.loggedIn, "true", {
        path: "/",
        secure: process.env.NODE_ENV === "production",
        sameSite: "lax",
      });
      showToast(TOAST_TYPES.success, "Login successful");
      router.replace("/");
      router.refresh();
    },

    onError: (error: IApiError) => {
      Sentry.captureException("Login credentials are invalid", {
        extra: {
          error,
        },
      });

      const errors = error?.response?.data;
      showToast(TOAST_TYPES.error, errors?.message ?? GLOBAL_ERROR_MESSAGE);
    },
  });

  const onSubmit = async (values: z.infer<typeof loginSchema>) => {
    login(values);
  };

  return {
    form,
    onSubmit,
    loginLoading,
    showPassword,
    togglePasswordVisibility,
  };
};

export default useLogin;
